import posthog from "posthog-js";
import { useEffect, useState } from "react";

/**
 * Gates every engagement-analytics UI surface (PostViewer metrics rows,
 * Settings follower counts, calendar quick-peek metrics, Posts list summary)
 * behind the `engagement-analytics-ui` PostHog flag.
 *
 * The collection engine (publish-txn docs, metrics sweep, follower
 * snapshots) runs regardless — this hides presentation only, so history
 * accumulates while the Meta reviews (FB read_insights, IG insights) are
 * pending. Rollout is a flag flip in PostHog; no deploy.
 *
 * Local dev builds bypass the flag entirely so the feature stays testable
 * end-to-end.
 */
const FLAG_KEY = "engagement-analytics-ui";
const DEV = process.env.NODE_ENV === "development";

// Module-level subscription: the calendar quick-peek mounts a consumer per
// opened peek, and posthog.onFeatureFlags never dedupes callbacks — so we
// subscribe once and fan out.
let current = DEV;
let subscribed = false;
let listeners: Array<(v: boolean) => void> = [];

function ensureSubscribed() {
  if (DEV || subscribed) return;
  subscribed = true;
  current = posthog.isFeatureEnabled(FLAG_KEY) === true;
  posthog.onFeatureFlags(() => {
    current = posthog.isFeatureEnabled(FLAG_KEY) === true;
    listeners.forEach((l) => l(current));
  });
}

export function useAnalyticsUiEnabled(): boolean {
  const [enabled, setEnabled] = useState<boolean>(() => {
    ensureSubscribed();
    return current;
  });
  useEffect(() => {
    if (DEV) return;
    const listener = (v: boolean) => setEnabled(v);
    listeners.push(listener);
    setEnabled(current);
    return () => {
      listeners = listeners.filter((l) => l !== listener);
    };
  }, []);
  return enabled;
}
